import { auth, firebaseProjectId } from "../firebase/firebase";

const LOCAL_API_REGION = "europe-west1";
const LOCAL_API_HOST = "http://127.0.0.1:5001";

function normalizeBaseUrl(value) {
  return String(value || "").trim().replace(/\/+$/, "");
}

function isLoopbackHostname(hostname) {
  const normalizedHostname = String(hostname || "").trim().toLowerCase();

  return (
    normalizedHostname === "localhost" ||
    normalizedHostname === "127.0.0.1" ||
    normalizedHostname === "0.0.0.0" ||
    normalizedHostname === "::1"
  );
}

function isPrivateIpv4Hostname(hostname) {
  const normalizedHostname = String(hostname || "").trim();

  if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(normalizedHostname)) {
    return false;
  }

  const octets = normalizedHostname
    .split(".")
    .map((value) => Number.parseInt(value, 10));

  if (octets.some((value) => !Number.isInteger(value) || value < 0 || value > 255)) {
    return false;
  }

  if (octets[0] === 10) {
    return true;
  }

  if (octets[0] === 192 && octets[1] === 168) {
    return true;
  }

  return octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31;
}

function shouldUseLocalApiHost(hostname) {
  const normalizedHostname = String(hostname || "").trim().toLowerCase();

  return isLoopbackHostname(normalizedHostname) || isPrivateIpv4Hostname(normalizedHostname);
}

export function resolveKarangoApiBaseUrl() {
  const explicitBaseUrl = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL);

  if (typeof window === "undefined") {
    return explicitBaseUrl;
  }

  const hostname = String(window.location.hostname || "").trim().toLowerCase();
  const hostedOriginBaseUrl =
    typeof window.location.origin === "string" && window.location.origin !== "null"
      ? normalizeBaseUrl(`${window.location.origin}/api`)
      : "";
  const runtimeOriginBaseUrl = shouldUseLocalApiHost(hostname)
    ? `http://${hostname}:5001/${firebaseProjectId}/${LOCAL_API_REGION}/api`
    : hostedOriginBaseUrl;

  if (explicitBaseUrl) {
    let explicitHostname = "";

    try {
      explicitHostname = new URL(explicitBaseUrl).hostname;
    } catch (error) {
      return explicitBaseUrl;
    }

    // If the app is opened from another device on the LAN, ignore
    // a loopback-only API URL and reuse the current host instead.
    if (!isLoopbackHostname(hostname) && isLoopbackHostname(explicitHostname)) {
      return runtimeOriginBaseUrl;
    }

    return explicitBaseUrl;
  }

  if (!runtimeOriginBaseUrl) {
    return "";
  }

  if (isLoopbackHostname(hostname)) {
    return `${LOCAL_API_HOST}/${firebaseProjectId}/${LOCAL_API_REGION}/api`;
  }

  return runtimeOriginBaseUrl;
}

export function isKarangoApiEnabled() {
  return Boolean(resolveKarangoApiBaseUrl());
}

async function parseApiResponse(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error("Reponse API invalide.");
  }
}

async function resolveAuthHeaders(firebaseUser) {
  const currentUser = firebaseUser || auth.currentUser;

  if (!currentUser || typeof currentUser.getIdToken !== "function") {
    const error = new Error("Connecte-toi pour acceder a cette section.");
    error.code = "auth/required";
    error.status = 401;
    throw error;
  }

  const token = await currentUser.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
  };
}

// sessionGuard (optionnel) : renvoie false des que la session de depart n'est
// plus la session courante (voir core/auth/authSession). Verifie avant chaque
// envoi, retry ou renouvellement de jeton, et avant de rendre la reponse :
// une mutation d'un compte ne part jamais avec l'identite d'un autre et une
// reponse tardive n'est jamais rendue a la session suivante.
function assertSessionCurrent(sessionGuard) {
  if (typeof sessionGuard !== "function" || sessionGuard()) return;
  const error = new Error("La session a change pendant la requete.");
  error.code = "auth/session-changed";
  error.status = 0;
  throw error;
}

const REQUEST_TIMEOUT_MS = 10_000;
const RETRYABLE_STATUS_CODES = new Set([500, 502, 503, 504]);
const MAX_RETRIES = 2;

async function fetchWithTimeout(url, options) {
  const controller = new AbortController();
  const callerSignal = options.signal;

  let callerAbortListener;
  if (callerSignal) {
    callerAbortListener = () => controller.abort(callerSignal.reason);
    callerSignal.addEventListener("abort", callerAbortListener);
  }

  const timeoutId = setTimeout(
    () => controller.abort(new Error("La requete a expire.")),
    REQUEST_TIMEOUT_MS
  );

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
    if (callerSignal && callerAbortListener) {
      callerSignal.removeEventListener("abort", callerAbortListener);
    }
  }
}

async function fetchWithRetry(url, options, maxRetries = MAX_RETRIES, sessionGuard = null) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 300 * 2 ** (attempt - 1)));
    }
    assertSessionCurrent(sessionGuard);

    try {
      const response = await fetchWithTimeout(url, options);
      if (attempt < maxRetries && RETRYABLE_STATUS_CODES.has(response.status)) {
        lastError = new Error(`HTTP ${response.status}`);
        continue;
      }
      return response;
    } catch (error) {
      if (error?.name === "AbortError" || options.signal?.aborted) {
        throw error;
      }
      lastError = error;
    }
  }

  throw lastError;
}

export async function requestKarangoApi(path, options = {}) {
  const {
    body,
    firebaseUser = null,
    headers: rawHeaders,
    onRequestStarted,
    optionalAuth = false,
    requiresAuth = false,
    retryable = true,
    sessionGuard = null,
    ...fetchOptions
  } = options;
  const baseUrl = resolveKarangoApiBaseUrl();

  if (!baseUrl) {
    throw new Error("API KARANGO non configuree.");
  }

  const headers = {
    Accept: "application/json",
    ...(rawHeaders || {}),
  };

  if (requiresAuth) {
    assertSessionCurrent(sessionGuard);
    Object.assign(headers, await resolveAuthHeaders(firebaseUser));
  } else if (optionalAuth) {
    try {
      Object.assign(headers, await resolveAuthHeaders(firebaseUser));
    } catch (error) {
      // Public endpoints can enrich their response when a token is available,
      // but they must stay readable for anonymous visitors.
    }
  }

  const hasJsonBody =
    body !== undefined &&
    body !== null &&
    !(body instanceof FormData);

  if (hasJsonBody && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const serializedBody = hasJsonBody ? JSON.stringify(body) : body;

  // Some mobile flows need to tell the user that it is now safe to put the
  // app in the background. Fire this only once, after auth/body preparation
  // and immediately before the first network request (never on a retry).
  if (typeof onRequestStarted === "function") {
    onRequestStarted();
  }

  const maxRetries = retryable ? MAX_RETRIES : 0;
  let response = await fetchWithRetry(`${baseUrl}${path}`, {
    ...fetchOptions,
    headers,
    body: serializedBody,
  }, maxRetries, sessionGuard);

  if (response.status === 401 && requiresAuth) {
    // Avec une garde, jamais le compte courant a la place du compte de depart.
    assertSessionCurrent(sessionGuard);
    const currentUser = firebaseUser || auth?.currentUser;
    if (currentUser?.getIdToken) {
      let freshToken = null;
      try {
        freshToken = await currentUser.getIdToken(true);
      } catch {
        // refresh failed — proceed with the original 401
      }
      if (freshToken) {
        headers.Authorization = `Bearer ${freshToken}`;
        try {
          response = await fetchWithRetry(`${baseUrl}${path}`, {
            ...fetchOptions,
            headers,
            body: serializedBody,
          }, maxRetries, sessionGuard);
        } catch (error) {
          if (error?.code === "auth/session-changed") throw error;
          // replay failed — proceed with the original 401
        }
      }
    }
  }

  const payload = await parseApiResponse(response);
  assertSessionCurrent(sessionGuard);

  if (!response.ok) {
    const error = new Error(
      payload?.error?.message || `Requete API echouee (${response.status}).`
    );
    error.code = payload?.error?.code || "api/request-failed";
    error.status = response.status;
    error.details = payload?.error?.details || null;
    throw error;
  }

  return payload;
}
